import React from "react";
import { useI18n } from "@/i18n";
import { HERO_IMAGE, LOGO_URL } from "@/constants";
import { ArrowRight, Sparkles } from "lucide-react";

export default function Hero() {
  const { t } = useI18n();
  return (
    <section
      className="relative pt-20 sm:pt-24 pb-20 sm:pb-28 overflow-hidden noise-overlay"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_IMAGE}
          alt=""
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-[#0A0A0A]" />
        <div className="absolute inset-0 grid-bg opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Widescreen logo banner */}
        <div
          data-testid="hero-logo-banner"
          className="relative mx-auto animate-fade-up"
        >
          <div className="relative rounded-sm bg-gradient-to-br from-brand-blue/40 via-white/5 to-transparent p-[1px] glow-blue">
            <div className="relative overflow-hidden rounded-sm bg-[#050505]">
              <img
                src={LOGO_URL}
                alt="All Possible 3D Solutions"
                className="block w-full h-[clamp(220px,40vw,560px)] object-cover object-center"
              />
              {/* top + bottom feathering for cinematic feel */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/90 to-transparent" />
              {/* eyebrow ribbon */}
              <div className="absolute top-4 left-4 sm:top-6 sm:left-6 inline-flex items-center gap-2 font-mono-accent text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-brand-blue-light border border-brand-blue/40 px-3 py-1.5 rounded-sm bg-black/60 backdrop-blur-sm">
                <Sparkles size={12} />
                {t.hero.eyebrow}
              </div>
              {/* filament animation strip */}
              <div className="filament-bar absolute left-0 right-0 bottom-0 h-1.5 animate-filament" />
            </div>
          </div>
          <div className="absolute -inset-x-4 -bottom-6 -z-10 h-24 bg-brand-blue/20 blur-3xl" />
        </div>

        {/* Headline + copy + CTA */}
        <div className="mt-14 grid lg:grid-cols-12 gap-8 lg:gap-12 items-end animate-fade-up [animation-delay:150ms]">
          <div className="lg:col-span-8">
            <h1
              data-testid="hero-heading"
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]"
            >
              {t.hero.title_1}
              <br />
              <span className="text-brand-blue-light">{t.hero.title_2}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-zinc-300 leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#catalog"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-light text-white px-6 py-3.5 uppercase tracking-widest text-xs font-bold border border-brand-blue-light/60 hover:shadow-[0_0_20px_rgba(0,85,255,0.55)] transition-all font-mono-accent"
              >
                {t.hero.cta_primary} <ArrowRight size={14} />
              </a>
              <a
                href="#catalog"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-transparent border border-white/20 text-white hover:bg-white/5 hover:border-white/40 px-6 py-3.5 uppercase tracking-widest text-xs transition-all font-mono-accent"
              >
                {t.hero.cta_secondary}
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8">
            <Stat value="10+" label={t.hero.stat_1} />
            <Stat value="4.9★" label={t.hero.stat_2} />
            <Stat value={t.hero.stat_3_value} label={t.hero.stat_3} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-heading text-2xl sm:text-3xl font-black text-white">{value}</div>
      <div className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
        {label}
      </div>
    </div>
  );
}
